// Typed wrappers for the go-pim per-company product catalog.
//
// Money handling (money is bigint end-to-end): unit_rate_minor is bigint. The
// apiClient's transformResponse parses with json-bigint, so an integer that
// exceeds Number.MAX_SAFE_INTEGER returns as bigint while smaller ints stay as
// number — which is why the Raw type is `number | bigint`. The BigInt(...)
// wrappers below normalise both branches to bigint (a no-op when already
// bigint). On the request side, json-bigint serialises bigint as a plain JSON
// integer, so unit_rate_minor rides the wire without a Number() truncation.
import { apiClient } from "./client";

export type BusinessType = "donation" | "livestock" | "solar" | "generic";
export type TaxScheme = "none" | "gst" | "solar_70_30";

/** RawProduct — JSON shape after json-bigint parse (money is number | bigint). */
interface RawProduct {
  id: string;
  company_id?: string;
  catalog_id?: string;
  name: string;
  description: string;
  category: string;
  business_type: BusinessType;
  hsn_sac: string;
  unit: string;
  unit_rate_minor: number | bigint;
  currency: string;
  tax_scheme: TaxScheme;
  gst_rate_bps: number;
  attributes: Record<string, string> | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Product — typed record exposed to UI consumers (unit_rate_minor: bigint). */
export interface Product {
  id: string;
  company_id?: string;
  /**
   * The catalog grouping this product sits in, or "" for UNGROUPED — a real and
   * permanent state, not a missing value. Always present on the wire (go-pim
   * emits the key even when empty).
   *
   * READ-ONLY through the product write payload: PUT /v1/products/:id ignores
   * it (see ProductWritePayload). Change it with moveProductToCatalog.
   */
  catalog_id: string;
  name: string;
  description: string;
  category: string;
  business_type: BusinessType;
  hsn_sac: string;
  unit: string;
  /** Minor units (e.g. paise) as bigint — never parseFloat. Display via fmtCcyMinor. */
  unit_rate_minor: bigint;
  currency: string;
  tax_scheme: TaxScheme;
  gst_rate_bps: number;
  attributes: Record<string, string>;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

function normalizeProduct(r: RawProduct): Product {
  return {
    ...r,
    // Older go-pim builds predate catalogs and omit the key entirely; normalise
    // that to "" (ungrouped) so consumers never branch on undefined.
    catalog_id: r.catalog_id ?? "",
    unit_rate_minor: BigInt(r.unit_rate_minor),
    attributes: r.attributes ?? {},
  };
}

/**
 * Fields accepted by POST /v1/companies/:id/products + PUT /v1/products/:id.
 *
 * catalog_id is honoured on CREATE only. The update route is a full-row replace
 * and deliberately ignores it, pinning the stored value, so that a client which
 * does not send the field cannot blank a product's grouping. To move a product
 * between catalogs use moveProductToCatalog (PUT /v1/products/:id/catalog).
 */
export interface ProductWritePayload {
  name: string;
  description: string;
  category: string;
  business_type: BusinessType;
  hsn_sac: string;
  unit: string;
  unit_rate_minor: bigint;
  currency: string;
  tax_scheme: TaxScheme;
  gst_rate_bps: number;
  attributes: Record<string, string>;
  active: boolean;
  /** Create-only; ignored by updateProduct. "" means ungrouped. */
  catalog_id?: string;
}

export interface ListProductsParams {
  active_only?: boolean;
  /**
   * Narrow to one catalog by id, or the literal "none" for the UNGROUPED
   * bucket. Omitting it lists every product regardless of catalog.
   *
   * "none" is a sentinel because a query string cannot express the difference
   * between an absent and an empty value, so `catalog_id=` (empty) means "no
   * filter". It can never shadow a real catalog: ids are `cat_<hex>`.
   */
  catalog_id?: string;
}

/** The ?catalog_id= value selecting products that are in no catalog. */
export const UNGROUPED = "none";

/**
 * listProducts — GET /v1/companies/:companyID/products?active_only=
 * ASSUMED SHAPE: `{ products: RawProduct[] }`.
 */
export async function listProducts(
  companyId: string,
  params: ListProductsParams = {},
): Promise<Product[]> {
  const resp = await apiClient.get<{ products: RawProduct[] }>(
    `/v1/companies/${companyId}/products`,
    { params },
  );
  return (resp.data.products ?? []).map(normalizeProduct);
}

/** getProduct — GET /v1/products/:id */
export async function getProduct(id: string): Promise<Product> {
  const resp = await apiClient.get<RawProduct>(`/v1/products/${id}`);
  return normalizeProduct(resp.data);
}

/** createProduct — POST /v1/companies/:companyID/products */
export async function createProduct(
  companyId: string,
  payload: ProductWritePayload,
): Promise<Product> {
  const resp = await apiClient.post<RawProduct>(
    `/v1/companies/${companyId}/products`,
    payload,
  );
  return normalizeProduct(resp.data);
}

/** updateProduct — PUT /v1/products/:id */
export async function updateProduct(
  id: string,
  payload: ProductWritePayload,
): Promise<Product> {
  const resp = await apiClient.put<RawProduct>(`/v1/products/${id}`, payload);
  return normalizeProduct(resp.data);
}

/** deleteProduct — DELETE /v1/products/:id */
export async function deleteProduct(id: string): Promise<void> {
  await apiClient.delete(`/v1/products/${id}`);
}

// ── Resolve (price preview) ─────────────────────────────────────────────────
// POST /v1/companies/:companyID/products/resolve
// Body: [{ item_id, qty }] → priced lines + totals (all minor units, bigint).

export interface ResolveItem {
  item_id: string;
  qty: number;
}

interface RawResolveLine {
  item_id: string;
  name?: string;
  qty: number;
  unit_rate_minor: number | bigint;
  line_total_minor: number | bigint;
}

interface RawResolveResult {
  lines: RawResolveLine[];
  subtotal_minor: number | bigint;
  tax_minor: number | bigint;
  total_minor: number | bigint;
  currency: string;
}

export interface ResolveLine {
  item_id: string;
  name?: string;
  qty: number;
  unit_rate_minor: bigint;
  line_total_minor: bigint;
}

export interface ResolveResult {
  lines: ResolveLine[];
  subtotal_minor: bigint;
  tax_minor: bigint;
  total_minor: bigint;
  currency: string;
}

/**
 * resolveProducts — POST /v1/companies/:companyID/products/resolve
 * Prices a cart of {item_id, qty} into lines + subtotal/tax/total (minor units).
 */
export async function resolveProducts(
  companyId: string,
  items: ResolveItem[],
): Promise<ResolveResult> {
  const resp = await apiClient.post<RawResolveResult>(
    `/v1/companies/${companyId}/products/resolve`,
    items,
  );
  const r = resp.data;
  return {
    currency: r.currency,
    subtotal_minor: BigInt(r.subtotal_minor),
    tax_minor: BigInt(r.tax_minor),
    total_minor: BigInt(r.total_minor),
    lines: (r.lines ?? []).map((l) => ({
      item_id: l.item_id,
      name: l.name,
      qty: l.qty,
      unit_rate_minor: BigInt(l.unit_rate_minor),
      line_total_minor: BigInt(l.line_total_minor),
    })),
  };
}
